import React from 'react';
import styled from 'styled-components';
import {HomepageMainIntroProps, IndustryProps} from './HomepageHeader.types';
import {
    AboveFoldScrollIndicator,
    BgImage,
    GridContainer,
    Image,
    SectionInnerGridContent,
    TextLink,
    Typography,
    VerticalSpacer,
    Button
} from '../../elements/';
import {AnimateOnViewWrap, UeLink} from '../../wraps';
import {motion, useMotionValue, useTransform} from 'framer-motion';
import {useScroll} from '../../../shared';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';

const HeroGradient = styled.div`
    height: 100%;
    background: linear-gradient(
        0deg,
        ${(p) => p.theme.palette.background.default},
        rgba(13, 16, 35, 0) 100%
    );
`;

const IntroWrap = styled.div`
    position: relative;
    top: 0;
    height: 100vh;
    left: 0;
    right: 0;
`;

const Intro = styled.div`
    bottom: 0;
    z-index: 2;
    position: absolute;
    transform: translateY(-60%);
`;

const Universe = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    z-index: 1;
`;

const Industries = styled.div``;

const IndustryCard = styled.div`
    position: relative;
    overflow: visible;

    &.even {
        margin-right: ${(p) => p.theme.spacing(10)}px;
        ${(p) => p.theme.breakpoints.down('xs')} {
            margin-right: ${(p) => p.theme.spacing(5)}px;
        }
        .content {
            ${(p) => p.theme.breakpoints.down('xs')} {
                padding-right: 0;
            }
        }
    }

    &.odd {
        margin-left: ${(p) => p.theme.spacing(10)}px;
        ${(p) => p.theme.breakpoints.down('xs')} {
            margin-left: ${(p) => p.theme.spacing(5)}px;
        }
        .content {
            text-align: right;
            left: auto;
            right: 0;
            ${(p) => p.theme.breakpoints.down('xs')} {
                padding-left: 0;
            }
        }
    }

    .content {
        position: relative;
        height: auto;
        left: 0;
        padding: ${(p) => p.theme.spacing(4)}px;
        margin-top: -25px;
        padding-top: 0;
    }
`;
const HomepageSmallContainer = styled.div`
    position: relative;
    .background-image {
        height: 80vh;
    }
`;

interface HomepageIntroProps {
    introData?: HomepageMainIntroProps;
    industries?: IndustryProps[];
}

export const HomepageSmallHeader = ({
    introData = {},
    industries = [],
    
}: HomepageIntroProps): JSX.Element => {
    const {position} = useScroll();
    const scrollY = useMotionValue(0);
    scrollY.set(position.y);

    const isSm = useBreakpointDown('sm');

    const fadeHeader = useTransform(scrollY, [0, 300], [1, 0.2]);
    const slideHeader = useTransform(scrollY, [0, 1000], [0, -5]);

    const {title, image, scrollHref, scrollLabel} = introData;
    let universeElements: (JSX.Element | undefined)[] = [<div key="universeElements" />];

    if (industries) {
        universeElements = industries.map((industry, i) => {
            const accentColor: AccentColorOptions = industry.accentColor || 'sky';

            if (industry) {
                return (
                    <React.Fragment key={`universe-industry-${industry.label || i}`}>
                        <AnimateOnViewWrap delay={0.2} translateY={50}>
                            <IndustryCard className={`${i % 2 === 0 ? 'even' : 'odd'}`}>
                                <UeLink href={industry.url}>
                                    <Image src={industry.image} gradient={true} />
                                </UeLink>
                                <div className="content">
                                    <Typography variant="h4">{industry.label}</Typography>
                                    <VerticalSpacer variant="heading" />
                                    <TextLink href={industry.url} accentColor={accentColor}>
                                        <Typography variant="h2">{industry.tagLine}</Typography>
                                    </TextLink>
                                </div>
                            </IndustryCard>
                        </AnimateOnViewWrap>
                        <VerticalSpacer variant="heading" />
                    </React.Fragment>
                );
            }
        });
    }

    return (
        <HomepageSmallContainer>
            <motion.div
                style={{
                    opacity: fadeHeader,
                    translateY: slideHeader
                }}>
                <Universe>
                    {image && (
                        <BgImage img={image}>
                            <HeroGradient />
                        </BgImage>
                    )}
                </Universe>
            </motion.div>

            <IntroWrap>
                <Intro>
                    <GridContainer>
                        <SectionInnerGridContent>
                            {title && (
                                <AnimateOnViewWrap delay={0.85} translateY={-15}>
                                    <Typography variant="h1" petit={isSm}>
                                        {title}
                                    </Typography>
                                    <VerticalSpacer variant="heading" />
                                    {scrollHref && scrollLabel && <>
                                        <Button
                                            className="homepageHeroCTA"
                                            type="secondary"
                                            href={scrollHref}>
                                            {scrollLabel}
                                        </Button>
                                        <VerticalSpacer variant="feature" />
                                    </>}
                                    <AboveFoldScrollIndicator translateY={0} />
                                </AnimateOnViewWrap>
                            )}
                        </SectionInnerGridContent>
                    </GridContainer>
                </Intro>
            </IntroWrap>

            <Industries>
                <GridContainer>{universeElements}</GridContainer>
            </Industries>
        </HomepageSmallContainer>
    );
};
